import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, CreditCard, Mail, Copy, ExternalLink, RefreshCw, AlertCircle, Search } from 'lucide-react';

const formatCurrency = (cents, currency = 'usd') => {
  if (cents == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);
};

const formatDate = (epoch) => {
  if (!epoch) return '—';
  return new Date(epoch * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const StatusBadge = ({ status }) => {
  const map = {
    active: 'bg-emerald-100 text-emerald-700',
    trialing: 'bg-blue-100 text-blue-700',
    past_due: 'bg-amber-100 text-amber-700',
    canceled: 'bg-slate-200 text-slate-600',
    incomplete: 'bg-rose-100 text-rose-700',
  };
  return <Badge className={map[status] || 'bg-slate-100 text-slate-700'}>{status || 'none'}</Badge>;
};

const PlatformAdminBilling = () => {
  const { api } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [portalDialog, setPortalDialog] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [generatedLink, setGeneratedLink] = useState(null);

  useEffect(() => {
    fetchCustomers();
  }, [filter]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? '' : `?audience=${filter}`;
      const res = await api.get(`/admin/billing/customers${params}`);
      setCustomers(res.data.customers || []);
    } catch (err) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (customer) => {
    setDetailLoading(true);
    setDetail({ loading: true, customer });
    try {
      const customer_type = customer.type === 'organization' ? 'organization' : 'user';
      const res = await api.get(`/admin/billing/customer/${customer_type}/${customer.id}`);
      setDetail({ ...res.data, customer });
    } catch (err) {
      toast.error('Failed to load billing details');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const generatePortalLink = async (customer, delivery) => {
    setPortalLoading(true);
    setGeneratedLink(null);
    try {
      const customer_type = customer.type === 'organization' ? 'organization' : 'user';
      const res = await api.post('/admin/billing/portal-link', {
        customer_type,
        entity_id: customer.id,
        delivery,
        return_url: window.location.origin + '/admin',
      });
      setGeneratedLink(res.data);
      if (delivery.includes('email') && res.data.emailed_to?.length) {
        toast.success(`Portal link emailed to ${res.data.emailed_to.length} recipient(s)`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate portal link');
    } finally {
      setPortalLoading(false);
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  const filtered = customers.filter((c) => {
    const q = search.toLowerCase();
    return !q || (c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q));
  });

  return (
    <div className="space-y-6" data-testid="admin-billing-section">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-emerald-500" />
                Subscription Billing
              </CardTitle>
              <CardDescription>
                Live Stripe data for all subscribers — view, update, send portal links
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchCustomers} data-testid="billing-refresh">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <Tabs value={filter} onValueChange={setFilter}>
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="organization">Organizations</TabsTrigger>
                <TabsTrigger value="parent">Parents</TabsTrigger>
                <TabsTrigger value="christian">Christian</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative flex-1 min-w-[240px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="billing-search"
              />
            </div>
          </div>

          {loading ? (
            <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-500 mx-auto" /></div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-slate-500">
              <CreditCard className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              No Stripe customers yet. Customers will appear here once they subscribe.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((c) => (
                  <TableRow key={`${c.type}-${c.id}`} data-testid={`billing-row-${c.id}`}>
                    <TableCell className="font-medium">{c.name || '—'}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{c.type}</Badge></TableCell>
                    <TableCell className="text-slate-600 text-sm">{c.email || '—'}</TableCell>
                    <TableCell className="text-sm">{c.subscription_plan || 'free'}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => openDetail(c)} data-testid={`billing-view-${c.id}`}>
                          View
                        </Button>
                        <Button size="sm" onClick={() => { setPortalDialog(c); setGeneratedLink(null); }} data-testid={`billing-portal-${c.id}`}>
                          <Mail className="w-3 h-3 mr-1" /> Send Update Link
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Billing Details — {detail?.customer?.name}</DialogTitle>
            <DialogDescription>{detail?.customer?.email}</DialogDescription>
          </DialogHeader>
          {detailLoading || detail?.loading ? (
            <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-emerald-500" /></div>
          ) : detail?.has_billing === false ? (
            <div className="py-6 text-center text-slate-500">
              <AlertCircle className="w-10 h-10 mx-auto mb-3 text-amber-400" />
              No Stripe customer record yet. Send them a Update Link to create one.
            </div>
          ) : detail ? (
            <div className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs uppercase text-slate-500 font-medium mb-2">Subscription</p>
                  {detail.subscription ? (
                    <>
                      <div className="flex items-center justify-between mb-1">
                        <StatusBadge status={detail.subscription.status} />
                        <span className="text-sm text-slate-700">{formatCurrency(detail.subscription.amount, detail.subscription.currency)}/{detail.subscription.interval}</span>
                      </div>
                      <p className="text-xs text-slate-500">Renews {formatDate(detail.subscription.current_period_end)}</p>
                      {detail.subscription.cancel_at_period_end && (
                        <p className="text-xs text-rose-600 font-medium mt-1">Cancels at period end</p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-slate-500">No active subscription</p>
                  )}
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs uppercase text-slate-500 font-medium mb-2">Payment Method</p>
                  {detail.card ? (
                    <>
                      <p className="font-mono text-sm uppercase">{detail.card.brand} •••• {detail.card.last4}</p>
                      <p className="text-xs text-slate-500">Expires {String(detail.card.exp_month).padStart(2, '0')}/{detail.card.exp_year}</p>
                    </>
                  ) : (
                    <p className="text-sm text-slate-500">No card on file</p>
                  )}
                </div>
              </div>

              {detail.address && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs uppercase text-slate-500 font-medium mb-2">Billing Address</p>
                  <p className="text-sm">
                    {[detail.address.line1, detail.address.line2, detail.address.city, detail.address.state, detail.address.postal_code, detail.address.country].filter(Boolean).join(', ')}
                  </p>
                </div>
              )}

              <div>
                <p className="text-xs uppercase text-slate-500 font-medium mb-2">Recent Invoices</p>
                {detail.invoices?.length ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Number</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detail.invoices.map((inv) => (
                        <TableRow key={inv.id}>
                          <TableCell className="text-sm">{formatDate(inv.created)}</TableCell>
                          <TableCell className="text-sm font-mono">{inv.number || '—'}</TableCell>
                          <TableCell className="text-sm">{formatCurrency(inv.amount_paid || inv.amount_due, inv.currency)}</TableCell>
                          <TableCell><StatusBadge status={inv.status} /></TableCell>
                          <TableCell>
                            {inv.hosted_invoice_url && (
                              <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline text-sm inline-flex items-center gap-1">
                                Open <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-sm text-slate-500">No invoices yet</p>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Portal link generator dialog */}
      <Dialog open={!!portalDialog} onOpenChange={(open) => !open && setPortalDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send billing update link</DialogTitle>
            <DialogDescription>
              Generates a secure Stripe Customer Portal link for <strong>{portalDialog?.name}</strong> so they can update their card, billing address, or download invoices.
            </DialogDescription>
          </DialogHeader>
          {!generatedLink ? (
            <div className="space-y-3 py-2">
              <Button
                className="w-full justify-start"
                onClick={() => generatePortalLink(portalDialog, ['copy'])}
                disabled={portalLoading}
                data-testid="portal-copy-only"
              >
                {portalLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                Generate link only (copy)
              </Button>
              <Button
                className="w-full justify-start"
                variant="outline"
                onClick={() => generatePortalLink(portalDialog, ['email'])}
                disabled={portalLoading}
                data-testid="portal-email-only"
              >
                {portalLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                Email link to subscriber
              </Button>
              <Button
                className="w-full justify-start"
                variant="outline"
                onClick={() => generatePortalLink(portalDialog, ['copy', 'email'])}
                disabled={portalLoading}
                data-testid="portal-both"
              >
                {portalLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                Copy & email both
              </Button>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <p className="text-xs text-emerald-700 font-medium mb-1">Portal link generated</p>
                <p className="text-xs text-emerald-600 break-all font-mono">{generatedLink.url}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => copyLink(generatedLink.url)} data-testid="portal-copy-result">
                  <Copy className="w-3 h-3 mr-1" /> Copy
                </Button>
                <a href={generatedLink.url} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" variant="outline">
                    <ExternalLink className="w-3 h-3 mr-1" /> Open
                  </Button>
                </a>
              </div>
              {generatedLink.emailed_to?.length > 0 && (
                <p className="text-xs text-slate-600">Emailed to: {generatedLink.emailed_to.join(', ')}</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPortalDialog(null); setGeneratedLink(null); }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlatformAdminBilling;
